import { cn } from "@/lib/utils";
import { Check, Trash2 } from "lucide-react";
import { DateTime } from "luxon";
import React, { useCallback, useEffect, useState } from "react";
import { Await, useSearchParams } from "react-router-dom";
import { Button } from "../ui/button";
import { Combobox } from "../ui/combobox";
import { DatePickerRange } from "../ui/date-picker-range";
import DateTimeRangePicker from "../ui/datetime-range-picker";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Filter } from "./types";

interface ObjectFilterProps extends React.HTMLAttributes<HTMLDivElement> {
    filter: Filter;
    value?: string;
    onValueChange?: (value: string) => void;
    onValidate?: () => void;
}

function ObjectFilter({ filter, value, onValueChange, onValidate }: ObjectFilterProps) {
    if ((filter.type ?? "text") == "text") {
        return (
            <div className="grid w-full max-w-sm items-center space-y-2">
                <Label htmlFor={`${filter.field_name}`}>
                    <b>{filter.name ?? filter.field_name}</b> contains
                </Label>
                <Input
                    id={`${filter.field_name}`}
                    name={filter.field_name}
                    value={value ?? ""}
                    onChange={e => (onValueChange ? onValueChange(e.target.value) : null)}
                    onKeyDownCapture={e => {
                        if (e.key === "Enter" && onValidate) onValidate();
                    }}
                    className="max-w-sm"
                />
            </div>
        );
    } else if (filter.type == "select") {
        return (
            <div className="grid w-full max-w-sm items-center space-y-2">
                <Label htmlFor={filter.field_name}>
                    <b>{filter.name ?? filter.field_name}</b> is equal to{" "}
                </Label>
                <Select value={value || undefined} onValueChange={onValueChange}>
                    <SelectTrigger id={`${filter.field_name}`} className="max-w-sm">
                        <SelectValue placeholder="Selectionez une valeur" />
                    </SelectTrigger>
                    <SelectContent>
                        {filter.optionsFn ? (
                            <React.Suspense>
                                <Await resolve={filter.optionsFn()}>
                                    {(options: { value: string; label?: string }[]) =>
                                        options.map((option, index) => (
                                            <SelectItem key={`${option.value}_${index}`} value={option.value}>
                                                {option.label ?? option.value}
                                            </SelectItem>
                                        ))
                                    }
                                </Await>
                            </React.Suspense>
                        ) : (
                            (filter.options || []).map((option, index) => (
                                <SelectItem key={`${option.value}_${index}`} value={option.value}>
                                    {option.label ?? option.value}
                                </SelectItem>
                            ))
                        )}
                    </SelectContent>
                </Select>
            </div>
        );
    } else if (filter.type == "combobox") {
        return (
            <div className="grid w-full max-w-sm items-center space-y-2">
                <Label htmlFor={filter.field_name}>
                    <b>{filter.name ?? filter.field_name}</b> is one of the following values{" "}
                </Label>
                <Combobox
                    name={filter.field_name}
                    options={filter.options}
                    optionsUrl={filter.optionsUrl}
                    optionFormatter={filter.optionFormatter}
                    defaultValue={value}
                    onValueChange={onValueChange}
                    placeholder="Sélectionner une ou plusieurs valeurs"
                    className="w-full justify-between"
                    multiple
                />
            </div>
        );
    } else if (filter.type === "date-range") {
        const range = value ? { from: DateTime.fromISO(value).toJSDate() } : undefined;

        return (
            <div className="grid w-full max-w-sm items-center space-y-2">
                <Label htmlFor={filter.field_name}>
                    <b>{filter.name ?? filter.field_name}</b> is equal to{" "}
                </Label>
                <DatePickerRange
                    date={range}
                    setDate={date => {
                        if (date && date.from && date.to) {
                            const d_from = DateTime.fromJSDate(date.from);
                            const d_to = DateTime.fromJSDate(date.to);
                            if (d_from.isValid && d_to.isValid) {
                                onValueChange?.(`${d_from.toISODate() ?? ""},${d_to.toISODate() ?? ""}`);
                            }
                        } else {
                            onValueChange?.("");
                        }
                    }}
                    placeholder="Sélectionner une plage de dates"
                    className="w-full"
                />
            </div>
        );
    } else if (filter.type === "datetime-range") {
        const [from, to] = value?.split(",") ?? [];
        const range = value ? { from: DateTime.fromISO(from), to: DateTime.fromISO(to) } : undefined;

        return (
            <div className="grid w-full max-w-sm items-center space-y-2">
                <Label htmlFor={filter.field_name}>
                    <b>{filter.name ?? filter.field_name}</b> is between{" "}
                </Label>
                <DateTimeRangePicker
                    range={range}
                    onRangeChange={range => {
                        if (range && range.from && range.to && onValueChange) {
                            onValueChange(`${range.from.toISO({ includeOffset: true }) ?? ""},${range.to.toISO({ includeOffset: true }) ?? ""}`);
                        }
                    }}
                    className="w-full"
                    allowClear
                />
            </div>
        );
    }
}

function findFilterForSearchParam(filters: Filter[], key: string): Filter | undefined {
    const exact = filters.find(f => f.field_name === key);
    if (exact) return exact;
    return filters.find(f => `${f.field_name}__in` === key);
}

export function ObjectListFilters({ filters, activeFilters, setActiveFilters }: { filters: Filter[]; activeFilters: Filter[]; setActiveFilters: (activeFilters: Filter[]) => void }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [state, setState] = useState<"default" | "add">("add");
    const [values, setValues] = useState<Map<string, string>>(() => new Map());

    const applyFiltersToUrl = useCallback(
        (nextValues: Map<string, string>) => {
            const _searchParams = new URLSearchParams(searchParams);
            filters.forEach(filter => {
                const _value = nextValues.get(filter.field_name);
                if (_value) {
                    const separator = filter.multipleSeparator ?? ",";
                    const count = filter.type === "combobox" ? _value.split(separator).filter(Boolean).length : 0;
                    const hasExplicitLookup = filter.field_name.includes("__");
                    const paramName = count > 1 && !hasExplicitLookup ? `${filter.field_name}__in` : filter.field_name;

                    _searchParams.set(paramName, _value);
                    if (paramName !== filter.field_name) _searchParams.delete(filter.field_name);
                } else {
                    _searchParams.delete(filter.field_name);
                    _searchParams.delete(`${filter.field_name}__in`);
                }
            });
            if (_searchParams.toString() !== searchParams.toString()) {
                _searchParams.set("page", "1");
                setSearchParams(_searchParams);
            }
        },
        [filters, searchParams, setSearchParams]
    );

    useEffect(() => {
        const _values = new Map<string, string>();
        const _activeFilters: Filter[] = [];
        searchParams.forEach((value, key) => {
            const match = findFilterForSearchParam(filters, key);
            if (match) {
                _values.set(match.field_name, value);
                if (!_activeFilters.some(f => f.field_name === match.field_name)) {
                    _activeFilters.push(match);
                }
            }
        });
        setValues(_values);
        setActiveFilters(_activeFilters);
        setState(_activeFilters.length === 0 ? "add" : "default");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync from URL only
    }, [searchParams.toString()]);

    const updateFilterValue = (filter: Filter, value: string, applyImmediately = true) => {
        const nextValues = new Map(values);
        if (value) {
            nextValues.set(filter.field_name, value);
        } else {
            nextValues.delete(filter.field_name);
        }
        setValues(nextValues);
        if (applyImmediately) {
            applyFiltersToUrl(nextValues);
        }
    };

    const removeFilter = (filter: Filter) => {
        const nextActive = activeFilters.filter(f => f !== filter);
        const nextValues = new Map(values);
        nextValues.delete(filter.field_name);
        setActiveFilters(nextActive);
        setValues(nextValues);
        applyFiltersToUrl(nextValues);
        if (nextActive.length === 0) {
            setState("add");
        }
    };

    return (
        <div>
            {state == "add" ? (
                <>
                    <h4 className="mb-2 font-semibold">Select a property</h4>
                    <ul className="list-none">
                        {filters
                            .filter(f => !f.hidden)
                            .map((filter, index) => (
                                <li
                                    key={`${filter.field_name}_${index}`}
                                    className={cn("text-md w-full cursor-pointer rounded-sm px-4 py-2 transition-all hover:bg-accent")}
                                    onClick={() => {
                                        if (!activeFilters.some(f => f.field_name === filter.field_name)) {
                                            setActiveFilters([...activeFilters, filter]);
                                        }
                                        setState("default");
                                    }}
                                >
                                    <span className="flex flex-row items-center">
                                        {filter.name ?? filter.field_name}
                                        {activeFilters.some(f => f.field_name === filter.field_name) && <Check size={16} className="ml-1" />}
                                    </span>
                                </li>
                            ))}
                    </ul>
                </>
            ) : (
                <div className="space-y-3">
                    <div className="space-y-4">
                        {activeFilters.map((filter, index) => (
                            <div key={`${filter.field_name}_${index}`} className="relative rounded-sm border p-4 text-sm">
                                <ObjectFilter
                                    key={`${filter.field_name}_${index}`}
                                    filter={filter}
                                    className="cursor-pointer hover:bg-accent"
                                    value={values.get(filter.field_name)}
                                    onValueChange={value => updateFilterValue(filter, value)}
                                    onValidate={() => applyFiltersToUrl(values)}
                                />
                                <Trash2 className="absolute right-2 top-2 h-4 w-4 cursor-pointer" onClick={() => removeFilter(filter)} />
                            </div>
                        ))}
                    </div>
                    <div className="mt-5 flex justify-center space-x-4">
                        <Button size={"sm"} disabled={activeFilters.length == filters.length} onClick={() => setState("add")}>
                            Ajouter un filtre
                        </Button>
                        <Button
                            size={"sm"}
                            variant={"outline"}
                            disabled={activeFilters.length == 0}
                            onClick={() => {
                                setActiveFilters([]);
                                const emptyValues = new Map<string, string>();
                                setValues(emptyValues);
                                applyFiltersToUrl(emptyValues);
                                setState("add");
                            }}
                        >
                            Effacer les filtres
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
